import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LanguageCode, SurfaceLabelByLang } from '../../models/common/language.enum';
import { MembershipLabelByLang } from '../../models/common/membership.enum';
import { isCourtBasedSport } from '../../shared/sport-terminology';

interface SurfaceTermReplacementContext {
  label: string;
  pluralLabel: string;
  membershipLabel: string;
  membershipPluralLabel: string;
  lang: LanguageCode;
  originalMembership: string;
  originalMemberships: string;
}

@Injectable({
  providedIn: 'root',
})
export class SurfaceLabelService implements OnDestroy {
  private sportId: number | null = null;
  private readonly langSub: Subscription;

  constructor(private readonly translate: TranslateService) {
    // Re-apply overrides whenever language changes
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.applySurfaceLabel();
    });

    // Ensure initial override for current language
    // Wait a bit for translations to load
    setTimeout(() => {
      this.applySurfaceLabel();
    }, 100);
  }

  /**
   * Set the current sport id (e.g. from tenant context) and refresh labels.
   */
  setSportId(id: number | string | null | undefined) {
    const parsed =
      id === null || id === undefined || id === '' ? null : Number(id);
    this.sportId =
      parsed !== null && Number.isFinite(parsed) ? parsed : null;
    this.applySurfaceLabel();
  }

  /**
   * Resolve the current surface label text for the active language.
   */
  get currentLabel(): string {
    const lang = this.resolveLanguageCode(this.translate.getCurrentLang());
    const surfaceMap =
      SurfaceLabelByLang[lang] || SurfaceLabelByLang[LanguageCode.En];
    return this.usesCourtSurface() ? surfaceMap.court : surfaceMap.table;
  }

  /**
   * Resolve the current plural surface label text for the active language.
   */
  get currentPluralLabel(): string {
    const lang = this.resolveLanguageCode(this.translate.getCurrentLang());
    const surfaceMap =
      SurfaceLabelByLang[lang] || SurfaceLabelByLang[LanguageCode.En];
    return this.usesCourtSurface() ? surfaceMap.courts : surfaceMap.tables;
  }
  get currentMembershipLabel(): string {
  const lang = this.resolveLanguageCode(this.translate.getCurrentLang());
  const membermap =
    MembershipLabelByLang[lang] || MembershipLabelByLang[LanguageCode.En];

  return this.usesCourtSurface() ? membermap.license : membermap.membership;
}

get currentMembershipPluralLabel(): string {
  const lang = this.resolveLanguageCode(this.translate.getCurrentLang());
  const membermap =
    MembershipLabelByLang[lang] || MembershipLabelByLang[LanguageCode.En];

  return this.usesCourtSurface() ? membermap.licenses : membermap.memberships;
}

  /** Expose for templates / components that should not duplicate sport_id checks. */
  isCourtBasedSurface(): boolean {
    return isCourtBasedSport(this.sportId);
  }


  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /** True for Padel, Tennis, and any future court-based sport_id. */
  private usesCourtSurface(): boolean {
    return isCourtBasedSport(this.sportId);
  }

  private resolveLanguageCode(lang?: string): LanguageCode {
    const key = (lang || this.translate.getFallbackLang() || 'en').toLowerCase();
    const aliasMap: Record<string, LanguageCode> = {
      en: LanguageCode.En,
      english: LanguageCode.En,
      no: LanguageCode.No,
      norway: LanguageCode.No,
      'nb-no': LanguageCode.No,
      nb: LanguageCode.No,
      sv: LanguageCode.Sv,
      sweden: LanguageCode.Sv,
      swedan: LanguageCode.Sv,
      sw: LanguageCode.Sv,
      es: LanguageCode.Es,
      spain: LanguageCode.Es,
      sp: LanguageCode.Es,
      fr: LanguageCode.Fr,
      french: LanguageCode.Fr,
      cs: LanguageCode.Cs,
      czech: LanguageCode.Cs,
      da: LanguageCode.Da,
      danish: LanguageCode.Da,
      de:LanguageCode.De,
      it:LanguageCode.It,
    };
    return aliasMap[key] || LanguageCode.En;
  }

  /**
   * Override i18n keys so any template using `table` / `table_no` / `tables` gets the
   * correct surface label without additional HTML changes.
   * Also replaces "table"/"tables" in translation values with the appropriate surface label.
   */
  private applySurfaceLabel() {
    const langKey = this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'en';
    const label = this.currentLabel;
    const pluralLabel = this.currentPluralLabel;
    const membershipLabel = this.currentMembershipLabel;
    const membershipPluralLabel = this.currentMembershipPluralLabel;
    

    // Create a map of overrides
    const overrides: Record<string, string> = {
      table: label,
      table_no: label,
      tables: pluralLabel,
      membership: membershipLabel,
      memberships: membershipPluralLabel,
    };

    // Directly override specific known keys that contain "table" in their values
    // This ensures they work even if translations aren't fully loaded
    const knownKeysWithTable = [
      'select_table',
      'filter_matches_table_wise',
      "choose_membership_type",
      "select_membership_status_to_filter_out",
      "membership_status",
      "choose_players_for_membership"
     // 'Table_View'
    ];

    // Get the language-specific membership words to search for
    const lang = this.resolveLanguageCode(this.translate.getCurrentLang());
    const memberMap = MembershipLabelByLang[lang] || MembershipLabelByLang[LanguageCode.En];
    const originalMembership = memberMap.membership;
    const originalMemberships = memberMap.memberships;

    knownKeysWithTable.forEach(key => {
      try {
        const originalValue = this.translate.instant(key);
        if (originalValue && originalValue !== key && typeof originalValue === 'string') {
          const modifiedValue = this.applySurfaceTermReplacements(originalValue, {
            label,
            pluralLabel,
            membershipLabel,
            membershipPluralLabel,
            lang,
            originalMembership,
            originalMemberships,
          });
          if (modifiedValue !== originalValue) {
            overrides[key] = modifiedValue;
          }
        }
      } catch (error: unknown) {
        console.debug('Surface label override skipped for key', key, error);
      }
    });

    if (this.usesCourtSurface()) {
      const courtMembershipTitles: Partial<Record<LanguageCode, string>> = {
        [LanguageCode.De]: 'Lizenz wählen',
        [LanguageCode.Fr]: 'Choisis une licence',
        [LanguageCode.It]: 'Scegli la licenza',
      };
      const courtMembershipTitle = courtMembershipTitles[lang];

      if (courtMembershipTitle) {
        overrides['choose_membership_type'] = courtMembershipTitle;
      }
    }

    // Always apply the overrides we've built so far (including known keys)
    this.translate.setTranslation(langKey, overrides, true);

    // Also process all translations if available (for any keys we might have missed)
    let translations: any = null;
    
    // Method 1: Direct access to translations object
    if ((this.translate as any).translations?.[langKey]) {
      translations = (this.translate as any).translations[langKey];
    }
    
    // Process all translations if available
    if (translations && typeof translations === 'object') {
      // Create a new overrides object for additional keys
      const additionalOverrides: Record<string, string> = {};
      this.processTranslationsIntoOverrides(translations, additionalOverrides, label, pluralLabel,membershipLabel, membershipPluralLabel);
      
      // Apply additional overrides if any were found
      if (Object.keys(additionalOverrides).length > 0) {
        this.translate.setTranslation(langKey, additionalOverrides, true);
      }
    } else {
      // Try async approach if translations aren't loaded yet
      // Use translate.get() to check if translations are loaded, then access directly
      setTimeout(() => {
        const asyncTranslations = (this.translate as any).translations?.[langKey];
        if (asyncTranslations && typeof asyncTranslations === 'object') {
          const additionalOverrides: Record<string, string> = {};
          this.processTranslationsIntoOverrides(asyncTranslations, additionalOverrides, label, pluralLabel,membershipLabel, membershipPluralLabel);
          
          if (Object.keys(additionalOverrides).length > 0) {
            this.translate.setTranslation(langKey, additionalOverrides, true);
          }
        }
      }, 200);
    }
  }

  /**
   * Process translations and replace "table"/"tables" with appropriate surface labels
   * Adds modified values to the overrides object
   */
  private processTranslationsIntoOverrides(
    translations: Record<string, any>,
    overrides: Record<string, string>,
    label: string,
    pluralLabel: string,
    membershipLabel: string,
    membershipPluralLabel: string
  ) {
    // Get the language-specific membership words to search for
    const lang = this.resolveLanguageCode(this.translate.getCurrentLang());
    const memberMap = MembershipLabelByLang[lang] || MembershipLabelByLang[LanguageCode.En];
    const originalMembership = memberMap.membership;
    const originalMemberships = memberMap.memberships;
    
    Object.keys(translations).forEach(key => {
      const value = translations[key];
      if (typeof value === 'string' && value.length > 0) {
        const modifiedValue = this.applySurfaceTermReplacements(value, {
          label,
          pluralLabel,
          membershipLabel,
          membershipPluralLabel,
          lang,
          originalMembership,
          originalMemberships,
        });
        if (modifiedValue !== value) {
          overrides[key] = modifiedValue;
        }
      }
    });
  }

  private applySurfaceTermReplacements(
    value: string,
    context: SurfaceTermReplacementContext,
  ): string {
    const {
      label,
      pluralLabel,
      membershipLabel,
      membershipPluralLabel,
      lang,
      originalMembership,
      originalMemberships,
    } = context;
    let modifiedValue = value;

    if (/\btables\b/i.test(modifiedValue)) {
      modifiedValue = modifiedValue.replace(/\btables\b/gi, (match) =>
        this.replaceWithCase(match, pluralLabel),
      );
    }

    if (/\btable\b/i.test(modifiedValue)) {
      modifiedValue = modifiedValue.replace(/\btable\b/gi, (match) =>
        this.replaceWithCase(match, label),
      );
    }

    const escapedOriginalMemberships = this.escapeRegex(originalMemberships);
    const pluralMembershipRegex = new RegExp(
      String.raw`\b${escapedOriginalMemberships}(?=s?\b|s[a-zäöüß])`,
      'gi',
    );
    if (pluralMembershipRegex.test(modifiedValue)) {
      modifiedValue = modifiedValue.replace(pluralMembershipRegex, (match) =>
        this.replaceWithCase(match, membershipPluralLabel),
      );
    }

    const escapedOriginalMembership = this.escapeRegex(originalMembership);
    const membershipRegex = new RegExp(
      String.raw`\b${escapedOriginalMembership}(?=s?\b|s[a-zäöüß])`,
      'gi',
    );
    if (membershipRegex.test(modifiedValue)) {
      modifiedValue = modifiedValue.replace(membershipRegex, (match) =>
        this.replaceWithCase(match, membershipLabel),
      );
    }

    if (lang === LanguageCode.En || originalMembership === 'membership') {
      if (/\bmemberships\b/i.test(modifiedValue)) {
        modifiedValue = modifiedValue.replace(/\bmemberships\b/gi, (match) =>
          this.replaceWithCase(match, membershipPluralLabel),
        );
      }

      if (/\bmembership\b/i.test(modifiedValue)) {
        modifiedValue = modifiedValue.replace(/\bmembership\b/gi, (match) =>
          this.replaceWithCase(match, membershipLabel),
        );
      }
    }

    return modifiedValue;
  }

  private replaceWithCase(source: string, replacement: string): string {
    if (source === source.toUpperCase()) {
      return replacement.toUpperCase();
    }
    if (
      source.length > 0 &&
      source.startsWith(source.charAt(0).toUpperCase()) &&
      source !== source.toUpperCase()
    ) {
      return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
    }
    return replacement.toLowerCase();
  }
  
  /**
   * Helper to escape special regex characters
   */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\\$&`);
  }
}

